import type { ManagedDevicesPayload, DeviceStatus } from "@noverik/contracts";
import { ChevronLeft, ChevronRight, Monitor, RefreshCcw, Search, ShieldCheck } from "lucide-react";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiRequest, ApiRequestError } from "../lib/api";

const statuses: Record<DeviceStatus, string> = {
  online: "Online",
  offline: "Offline",
  unknown: "Sem informação",
};

function relativeDate(value: string | null) {
  if (!value) return "Não informado";
  const parsed = new Date(value);
  return Number.isNaN(parsed.valueOf()) ? "Não informado" : parsed.toLocaleString("pt-BR");
}

export function DevicesPage() {
  const [data, setData] = useState<ManagedDevicesPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [organizationId, setOrganizationId] = useState(0);
  const [status, setStatus] = useState("");
  const [refresh, setRefresh] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page), limit: "25" });
    if (query) params.set("q", query);
    if (organizationId) params.set("organizationId", String(organizationId));
    if (status) params.set("status", status);
    try {
      const next = await apiRequest<ManagedDevicesPayload>("/agent/devices?" + params.toString());
      setData(next);
    } catch (reason) {
      setData(null);
      setError(reason instanceof ApiRequestError ? reason.message : "Não foi possível consultar os equipamentos.");
    } finally {
      setLoading(false);
    }
  }, [page, query, organizationId, status, refresh]);

  useEffect(() => { void load(); }, [load]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setPage(1);
    setQuery(searchInput.trim());
  };

  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">NOVERIK REMOTE</span>
          <h1>Equipamentos</h1>
          <p>Inventário sincronizado do MeshCentral, organizado por cliente do osTicket.</p>
        </div>
        <button className="button secondary" type="button" onClick={() => setRefresh(value => value + 1)} disabled={loading}>
          <RefreshCcw size={17} /> Atualizar lista
        </button>
      </div>

      <div className="remote-notice">
        <ShieldCheck size={18} />
        <span>{data?.remoteAccessIntegrated ? 'Acesso remoto disponível no detalhe dos equipamentos autorizados, em nova aba e com login individual do MeshCentral. A abertura do link fica registrada.' : 'Inventário somente leitura. O acesso remoto continuará protegido no MeshCentral até a liberação dos controles de autorização e auditoria.'}</span>
      </div>

      <details className="remote-onboarding">
        <summary>Como cadastrar um computador para um cliente?</summary>
        <ol>
          <li>Confirme ou crie o cliente em <Link to="/desk/organizacoes">Organizações</Link> no Desk e identifique o ID da organização osTicket.</li>
          <li>Em <a href="https://remoto.noverik.com.br/" target="_blank" rel="noopener noreferrer">Noverik Remote</a>, crie um grupo de dispositivos exclusivo do cliente e autorize a conta técnica <strong>noverik-sync</strong> apenas para detalhes do dispositivo.</li>
          <li>Uma única vez por grupo, vincule o ID do grupo <code>mesh//...</code> à organização osTicket usando o script seguro <code>map-group.sh</code> na VPS.</li>
          <li>Dentro desse grupo, use <strong>Adicionar Agente</strong> no MeshCentral e instale o agente como serviço em cada computador autorizado pelo cliente.</li>
          <li>Os equipamentos desse grupo aparecerão automaticamente aqui após a sincronização (aproximadamente 2 minutos). Não é necessário cadastrar cada computador no osTicket.</li>
        </ol>
        <p>Para filiais, use grupos separados associados à mesma organização. Acesso remoto exige permissões individuais adicionais.</p>
      </details>

      {data && (
        <>
          <div className="remote-stats" aria-label="Resumo de equipamentos">
            <div><span>Equipamentos no filtro</span><strong>{data.total}</strong></div>
            <div><span>Online</span><strong>{data.counts.online}</strong></div>
            <div><span>Offline</span><strong>{data.counts.offline}</strong></div>
            <div><span>Sem informação</span><strong>{data.counts.unknown}</strong></div>
          </div>
          <p className={data.stale ? "remote-updated stale" : "remote-updated"}>
            {data.stale ? "Atenção: sincronização desatualizada. " : ""}
            Última sincronização: {relativeDate(data.collectedAt)}
          </p>
        </>
      )}

      <div className="remote-controls">
        <form className="searchbox inline" onSubmit={submitSearch}>
          <Search size={17} />
          <input aria-label="Buscar equipamento" value={searchInput} onChange={event => setSearchInput(event.target.value)} placeholder="Computador, sistema ou cliente..." />
          <button type="submit" className="search-submit">Buscar</button>
        </form>
        <select aria-label="Filtrar organização" value={organizationId} onChange={event => { setPage(1); setOrganizationId(Number(event.target.value)); }}>
          <option value={0}>Todas as organizações</option>
          {(data?.organizations ?? []).map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
        </select>
        <select aria-label="Filtrar status" value={status} onChange={event => { setPage(1); setStatus(event.target.value); }}>
          <option value="">Todos os status</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          <option value="unknown">Sem informação</option>
        </select>
      </div>

      {loading && <div className="data-message">Carregando inventário...</div>}
      {error && <div className="data-message error">{error}</div>}
      {!loading && data?.total === 0 && (
        <div className="data-message">Nenhum equipamento vinculado encontrado. Confira o grupo MeshCentral e o mapeamento da organização.</div>
      )}
      {!loading && data && data.items.length > 0 && (
        <div className="remote-table-wrap">
          <table className="remote-table">
            <thead><tr><th>Equipamento</th><th>Cliente</th><th>Sistema</th><th>Situação</th><th>Último contato</th><th /></tr></thead>
            <tbody>{data.items.map(device => (
              <tr key={device.id}>
                <td><div className="remote-device-name"><Monitor size={18} /><strong>{device.hostname}</strong></div></td>
                <td>{device.organization.name}</td>
                <td>{device.operatingSystem ?? "Não informado"}</td>
                <td><span className={"remote-status " + device.status}><i />{statuses[device.status]}</span></td>
                <td>{relativeDate(device.lastSeenAt)}</td>
                <td><Link className="remote-detail-link" to={"/desk/equipamentos/" + device.id}>Inventário</Link></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {!loading && data && data.pages > 1 && (
        <div className="pagination">
          <span>Página {data.page} de {data.pages}</span>
          <div>
            <button className="button secondary" disabled={data.page <= 1} onClick={() => setPage(n => Math.max(1, n - 1))}><ChevronLeft size={16} /> Anterior</button>
            <button className="button secondary" disabled={data.page >= data.pages} onClick={() => setPage(n => n + 1)}>Próxima <ChevronRight size={16} /></button>
          </div>
        </div>
      )}
    </div>
  );
}
